/**
 * THROWAWAY DEMO CODE (Pillar procurement demo). Mutable state for the BoM
 * forecast: whether the Hollis alternate (BOM Rev C) is approved and whether
 * the planned orders were sent to the buyer board. The what-if scenario and
 * cell edits are local to the page. Resets through `resetBomDemo()`.
 */
import { createDemoStore } from '@/demo/core/create-demo-store';

export type BomRunState = 'open' | 'running' | 'done';

export interface BomState {
  /** The alternate swap: open, running its agent steps, or approved (Rev C). */
  swap: BomRunState;
  /** Sending planned orders to the buyer board. */
  send: BomRunState;
  /** Orders and value sent, recorded when the send completes. */
  sentOrders: number;
  sentValue: number;
}

function buildSeed(): BomState {
  return { swap: 'open', send: 'open', sentOrders: 0, sentValue: 0 };
}

export const bomStore = createDemoStore<BomState>(buildSeed);

const pendingTimers = new Set<ReturnType<typeof setTimeout>>();

/** Run `complete` after `ms`, unless the demo is reset first. */
export function scheduleBomCompletion(ms: number, complete: () => void): void {
  const timer = setTimeout(() => {
    pendingTimers.delete(timer);
    complete();
  }, ms);
  pendingTimers.add(timer);
}

export function startSwap(): void {
  bomStore.update((draft) => {
    draft.swap = 'running';
  });
}

/** The alternate is approved: BOM Rev C is published and the hold risk clears. */
export function completeSwap(): void {
  bomStore.update((draft) => {
    draft.swap = 'done';
  });
}

export function startSend(): void {
  bomStore.update((draft) => {
    draft.send = 'running';
  });
}

export function completeSend(orders: number, value: number): void {
  bomStore.update((draft) => {
    draft.send = 'done';
    draft.sentOrders = orders;
    draft.sentValue = value;
  });
}

export function resetBomDemo(): void {
  for (const timer of pendingTimers) clearTimeout(timer);
  pendingTimers.clear();
  bomStore.reset();
}
